import { motion } from "framer-motion";
import { Download, Chrome, Shield, CheckCircle, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export const ExtensionInstallButton: React.FC = () => {
  const { t } = useTranslation();
  const [browserType, setBrowserType] = useState<string>('chrome');
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Détecter le navigateur
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      setBrowserType('chrome');
    } else if (userAgent.includes('firefox')) {
      setBrowserType('firefox');
    } else if (userAgent.includes('edg')) {
      setBrowserType('edge');
    } else {
      setBrowserType('chrome'); // Par défaut
    }

    // Vérifier si l'extension est déjà installée
    // Cette vérification peut être personnalisée selon votre extension
    if ((window as any).chrome && (window as any).chrome.runtime) {
      try {
        // Tentative de communication avec l'extension
        (window as any).chrome.runtime.sendMessage('your-extension-id', {action: 'ping'}, (response : any) => {
          if (response && response.installed) {
            setIsInstalled(true);
          }
        });
      } catch (error) {
        // Extension non installée
      }
    }
  }, []);

  const getBrowserIcon = () => {
    switch (browserType) {
      case 'firefox':
        return Globe;
      case 'edge':
        return Globe;
      default:
        return Chrome;
    }
  };

  const getBrowserName = () => {
    switch (browserType) {
      case 'firefox':
        return 'Firefox';
      case 'edge':
        return 'Edge';
      default:
        return 'Chrome';
    }
  };

  const getStoreUrl = () => {
    switch (browserType) {
      case 'firefox':
        return 'https://addons.mozilla.org/firefox/addon/3wchat/';
      case 'edge':
        return 'https://microsoftedge.microsoft.com/addons/detail/3wchat/';
      default:
        return 'https://chrome.google.com/webstore/detail/3wchat/';
    }
  };

  const handleInstallClick = async () => {
    setIsInstalling(true);

    try {
      if (browserType === 'chrome' && (window as any).chrome && (window as any).chrome.webstore) {
        // Installation directe pour Chrome si possible
        (window as any).chrome.webstore.install(
          getStoreUrl(),
          () => {
            setIsInstalled(true);
            setIsInstalling(false);
          },
          (error : any) => {
            console.error('Installation failed:', error);
            // Fallback vers l'ouverture du store
            window.open(getStoreUrl(), '_blank');
            setIsInstalling(false);
          }
        );
      } else {
        // Redirection vers le store approprié
        window.open(getStoreUrl(), '_blank');
        
        // Simuler l'installation (dans un vrai cas, vous détecteriez l'installation)
        setTimeout(() => {
          setIsInstalling(false);
          // Optionnel: demander à l'utilisateur de confirmer l'installation
        }, 2000);
      }
    } catch (error) {
      console.error('Installation error:', error);
      window.open(getStoreUrl(), '_blank');
      setIsInstalling(false);
    }
  };

  const BrowserIcon = getBrowserIcon();

  if (isInstalled) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="inline-flex items-center space-x-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-6 py-3 rounded-2xl border border-green-200 dark:border-green-700"
      >
        <CheckCircle className="w-5 h-5" />
        <span className="font-medium">{t('extensionPage.button.installed')}</span>
      </motion.div>
    );
  }

  return (
    <motion.button
      onClick={handleInstallClick}
      disabled={isInstalling}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={`
        relative inline-flex items-center space-x-3 px-8 py-4 rounded-2xl font-semibold text-lg
        transition-all duration-300 shadow-lg hover:shadow-xl
        ${isInstalling 
          ? 'bg-gray-400 cursor-not-allowed' 
          : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
        }
        text-white group overflow-hidden
      `}
    >
      {/* Effet d'ondulation au hover */}
      <motion.div
        className="absolute inset-0 bg-white/20 rounded-2xl"
        initial={{ scale: 0, opacity: 0 }}
        whileHover={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Contenu du bouton */}
      <div className="relative flex items-center space-x-3">
        {isInstalling ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
          />
        ) : (
          <Download className="w-6 h-6 group-hover:animate-bounce" />
        )}
        
        <div className="flex items-center space-x-2">
          <BrowserIcon className="w-5 h-5" />
          <span>
            {isInstalling 
              ? t('extensionPage.button.installing') 
              : `${t('extensionPage.button.install')} ${getBrowserName()}`
            }
          </span>
        </div>
      </div>

      {/* Badge de sécurité */}
      <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
        <Shield className="w-3 h-3" />
      </div>
    </motion.button>
  );
};

// Composant wrapper avec informations additionnelles
export const ExtensionInstallSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-xl text-center"
    >
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {t('extensionPage.button.title')}
        </h3>
        <p className="text-gray-600 dark:text-gray-300 max-w-md mx-auto">
          {t('extensionPage.button.description')}
        </p>
      </div>

      <ExtensionInstallButton />

      <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <Shield className="w-4 h-4 text-green-500" />
          <span>{t('extensionPage.button.secure')}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Download className="w-4 h-4 text-blue-500" />
          <span>{t('extensionPage.button.free')}</span>
        </div>
        <div className="flex items-center space-x-1">
          <CheckCircle className="w-4 h-4 text-purple-500" />
          <span>{t('extensionPage.button.verified')}</span>
        </div>
      </div>

      {/* Instructions d'installation */}
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl"
      >
        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
          {t('extensionPage.button.instructions.title')}
        </h4>
        <ol className="text-left text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <li className="flex items-start space-x-2">
            <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
            <span>{t('extensionPage.button.instructions.step1')}</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
            <span>{t('extensionPage.button.instructions.step2')}</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
            <span>{t('extensionPage.button.instructions.step3')}</span>
          </li>
        </ol>
      </motion.div>
    </motion.div>
  );
};